import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Autosuggest from 'react-autosuggest';
import debouce from 'lodash/debounce';

// Actions
import {
  updateIntentQuery, clearIntentSuggestion, getIntentSuggestion, clearAllIntent,
} from '../actions/action-crawl-intent-topic';
import { crawlingSearchTopic } from '../actions/action-select-topic';

// Constants
import { CRAWL_INTENT_SEARCH_TOPIC } from '../constants/PropTypesShapes';

// lib
import l10n from '../../lib/reactPPl10nHelper';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';

const label = {
  placeholder: l10n.getContent('smarthelp/channelSelection.topicSelections.searchTopics.searchbox.placeholder'),
  emptySuggestion: l10n.getContent('smarthelp/channelSelection.topicSelections.searchTopics.searchbox.error'),
};

class IntentTopicBox extends Component {
  static getEmptrySuggestionWarning() {
    return [{ topic_name: label.emptySuggestion, empty: true }];
  }

  // Render parent layer for the each search result
  static renderParentSuggestion(suggestion) {
    if (!Array.isArray(suggestion.parents) || suggestion.parents.length < 1) {
      return null;
    }

    const parents = suggestion.parents.slice(0, suggestion.parents.length - 1);
    return parents.map((parent) => ((
      <span className="crawl-intent-parent">
        {parent}
        <span className="cw_icon cw_icon-small cw_icon-arrow-right-small crawl-intent-parent-arrow" aria-hidden="true" />
      </span>
    )
    ));
  }

  static renderEmptyIcon(suggestion) {
    if (!suggestion.empty) return null;
    return (<span className="cw_icon cw_icon-small cw_icon-attention-xsmall crawl-intent-parent-attention" aria-hidden="true" />);
  }

  // Render each suggestion in the dropdown list
  static renderSuggestion(suggestion) {
    return (
      <div className={`crawl-intent-topic ${suggestion.empty && 'crawl-intent-empty-topic'}`}>
        {IntentTopicBox.renderEmptyIcon(suggestion)}
        {IntentTopicBox.renderParentSuggestion(suggestion)}
        <span className="crawl-intent-topic-name">{suggestion.topic_name}</span>
      </div>
    );
  }

  static getSuggestionValue(suggestion) {
    return suggestion;
  }

  static onFocusHandler() {
    trackLink({ id: 'contactUsPageSearchBox', link: 'customer-intent-search-started' });
  }

  constructor(props) {
    super(props);

    this.MIN_LENGTH = 1; // min characters required for search

    this.onChange = this.onChange.bind(this);
    this.onClickHandler = this.onClickHandler.bind(this);
    this.debouncedGetIntentSuggestion = debouce(this.props.getIntentSuggestion, 300);

    // Auto Suggesion event bind this
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
  }

  handleClearKeyPress(event) {
    if (event.which === 13) {
      this.props.clearAllIntent();
    }
  }

  // suggest onChange event handler
  onChange(event, { newValue }) {
    if (event.type === 'change') {
      this.props.updateIntentQuery({ query: newValue });
    }
  }

  // Scroll intent topic box for mobile and tablet version
  onClickHandler(event) {
    if ((Page.isMobileLayout() || Page.isTabletLayout()) && !this.props.query && event.target) {
      event.target.scrollIntoView();
    }
  }

  // After users click the topic tree selection
  onSuggestionSelected(event, { suggestion }) {
    this.props.crawlingSearchTopic(suggestion);
  }

  // using Query to get the suggestion from Fuse.js Helper
  onSuggestionsFetchRequested({ value }) {
    if (value.length > this.MIN_LENGTH) {
      this.debouncedGetIntentSuggestion({ query: value, searchPool: this.props.searchTopicPool });
    }
  }

  // Event handler for search box losing focus
  onSuggestionsClearRequested() {
    this.props.clearIntentSuggestion();
  }

  getSuggestions() {
    if (this.props.isEmptySuggestion) {
      trackLink({ id: 'contactUsPageSearchBox', link: `customer-intent-search-no-results-found|${this.props.query}` });
    }
    return !this.props.isEmptySuggestion ? this.props.searchResult : IntentTopicBox.getEmptrySuggestionWarning();
  }

  renderClearButton() {
    if (!this.props.query) return null;

    return (
      <button
        className="crawl-intent-clear-btn"
        tabIndex="0"
        onKeyPress={(event) => this.handleClearKeyPress(event)}
        onClick={this.props.clearAllIntent}
      >
        <span className="cw_icon cw_icon-small cw_icon-close-small crawl-intent-clear-icon" aria-hidden="true" />
      </button>
    );
  }

  render() {
    const suggestions = this.getSuggestions();
    const inputProps = {
      placeholder: label.placeholder,
      value: this.props.query,
      onChange: this.onChange,
      onClick: this.onClickHandler,
      onFocus: IntentTopicBox.onFocusHandler,
      className: 'crawl-intent-input',
      maxLength: 250,
      id: 'crawl-intent-input-text',
    };

    return (
      <div className="crawl-intent-topic-container">
        <span className="cw_icon cw_icon-small cw_icon-search-small crawl-intent-search-icon" aria-hidden="true" />
        <label htmlFor="crawl-intent-input-text" className="label-crawl-intent-input-text sr-only">{label.placeholder}</label>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          getSuggestionValue={IntentTopicBox.getSuggestionValue}
          renderSuggestion={IntentTopicBox.renderSuggestion}
          inputProps={inputProps}
        />
        {this.renderClearButton()}
      </div>
    );
  }
}

IntentTopicBox.propTypes = {
  query: PropTypes.string.isRequired,
  searchResult: PropTypes.arrayOf(PropTypes.shape(CRAWL_INTENT_SEARCH_TOPIC)).isRequired,
  searchTopicPool: PropTypes.arrayOf(PropTypes.shape(CRAWL_INTENT_SEARCH_TOPIC)).isRequired,
  updateIntentQuery: PropTypes.func.isRequired,
  clearIntentSuggestion: PropTypes.func.isRequired,
  getIntentSuggestion: PropTypes.func.isRequired,
  crawlingSearchTopic: PropTypes.func.isRequired,
  clearAllIntent: PropTypes.func.isRequired,
  isEmptySuggestion: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    query: state.crawlIntentTopic.query,
    searchResult: state.crawlIntentTopic.searchResult,
    searchTopicPool: state.crawlIntentTopic.searchTopicPool,
    isEmptySuggestion: state.crawlIntentTopic.empty,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    updateIntentQuery, clearIntentSuggestion, getIntentSuggestion, clearAllIntent, crawlingSearchTopic,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(IntentTopicBox);
